/**
 * Central policy field configuration (BRD M1).
 * Add or reorder fields here — PolicyDetailView & PolicyForm pick them up automatically.
 * Visibility/required rules are driven by policy_type via showIf / requiredIf.
 */

export const POLICY_TYPE_OPTIONS = [
  { value: 'group_mediclaim', label: 'Group Mediclaim', color: 'bg-blue-100 text-blue-800' },
  { value: 'top_up', label: 'Top-up', color: 'bg-purple-100 text-purple-800' },
  { value: 'parents_policy', label: 'Parents Policy', color: 'bg-amber-100 text-amber-800' },
  { value: 'term_life', label: 'Term Life Insurance', color: 'bg-rose-100 text-rose-800' },
];

export const FLOATER_TYPE_OPTIONS = [
  { value: 'floater', label: 'Floater' },
  { value: 'non_floater', label: 'Non-floater' },
];

const typeIs = (...types) => (f) => types.includes(f.policy_type);
const typeNot = (...types) => (f) => !types.includes(f.policy_type);

/** Fields cleared in payload when hidden for the selected policy type */
export const TYPE_NULL_FIELDS = {
  group_mediclaim: [
    'deductible_amount', 'restoration_benefit',
    'max_parent_age', 'floater_type',
    'free_cover_limit', 'max_sum_assured', 'ci_rider_included', 'accident_rider_included', 'grace_period_days',
  ],
  top_up: [
    'maternity_coverage', 'maternity_sub_limit', 'day_care_procedures',
    'opd_coverage', 'ambulance_cover_limit', 'e_card_enabled', 'digital_claim_enabled', 'wellness_benefit',
    'max_parent_age', 'floater_type',
    'free_cover_limit', 'max_sum_assured', 'ci_rider_included', 'accident_rider_included', 'grace_period_days',
  ],
  parents_policy: [
    'maternity_coverage', 'maternity_sub_limit', 'max_child_age',
    'opd_coverage', 'ambulance_cover_limit', 'e_card_enabled', 'digital_claim_enabled', 'wellness_benefit',
    'deductible_amount', 'restoration_benefit',
    'free_cover_limit', 'max_sum_assured', 'ci_rider_included', 'accident_rider_included', 'grace_period_days',
  ],
  term_life: [
    'room_rent_limit', 'maternity_coverage', 'maternity_sub_limit', 'cashless_hospitals',
    'pre_existing_disease_rules', 'day_care_procedures', 'corporate_buffer', 'co_payment_percentage',
    'max_child_age', 'in_laws_eligible', 'lwp_suspend_days',
    'opd_coverage', 'ambulance_cover_limit', 'e_card_enabled', 'digital_claim_enabled', 'wellness_benefit',
    'deductible_amount', 'restoration_benefit',
    'max_parent_age', 'floater_type',
  ],
};

const BOOLEAN_NULL_DEFAULTS = new Set([
  'maternity_coverage', 'opd_coverage', 'e_card_enabled', 'digital_claim_enabled', 'wellness_benefit',
  'restoration_benefit', 'ci_rider_included', 'accident_rider_included', 'in_laws_eligible',
]);

export const POLICY_SECTIONS = [
  {
    id: 'company',
    title: 'Company Details',
    description: 'Employer and branch information',
    fields: [
      { key: 'company_name', label: 'Company Name', type: 'text', required: true, col: 2 },
      { key: 'group_company_name', label: 'Group Company', type: 'text', col: 1 },
      { key: 'branch', label: 'Branch', type: 'text', required: true },
      { key: 'gst_number', label: 'GST Number', type: 'text', required: true },
      { key: 'policy_number', label: 'Policy Number', type: 'text' },
      { key: 'policy_type', label: 'Policy Type', type: 'select', options: POLICY_TYPE_OPTIONS, required: true },
      { key: 'insurance_provider_id', label: 'Insurance Provider', type: 'provider', required: true },
    ],
  },
  {
    id: 'period',
    title: 'Policy Period',
    description: 'Active coverage window',
    fields: [
      { key: 'policy_start_date', label: 'Start Date', type: 'date', required: true },
      { key: 'policy_end_date', label: 'End Date', type: 'date', required: true },
      { key: 'waiting_period_days', label: 'Waiting Period (days)', type: 'number' },
      {
        key: 'grace_period_days',
        label: 'Premium Grace Period (days)',
        type: 'number',
        showIf: typeIs('term_life'),
      },
    ],
  },
  {
    id: 'coverage',
    title: 'Coverage & Benefits',
    description: 'Sum insured, limits and add-ons',
    fields: [
      { key: 'sum_insured', label: 'Sum Insured (INR)', type: 'currency', required: true },
      {
        key: 'deductible_amount',
        label: 'Deductible / Threshold (INR)',
        type: 'currency',
        showIf: typeIs('top_up'),
        requiredIf: typeIs('top_up'),
      },
      {
        key: 'restoration_benefit',
        label: 'Sum Insured Restoration',
        type: 'boolean',
        showIf: typeIs('top_up'),
      },
      {
        key: 'free_cover_limit',
        label: 'Free Cover Limit (INR)',
        type: 'currency',
        showIf: typeIs('term_life'),
        requiredIf: typeIs('term_life'),
      },
      {
        key: 'max_sum_assured',
        label: 'Max Sum Assured (INR)',
        type: 'currency',
        showIf: typeIs('term_life'),
        requiredIf: typeIs('term_life'),
      },
      {
        key: 'ci_rider_included',
        label: 'Critical Illness Rider Included',
        type: 'boolean',
        showIf: typeIs('term_life'),
      },
      {
        key: 'accident_rider_included',
        label: 'Accident Cover Rider Included',
        type: 'boolean',
        showIf: typeIs('term_life'),
      },
      {
        key: 'floater_type',
        label: 'Floater Type',
        type: 'select',
        options: FLOATER_TYPE_OPTIONS,
        showIf: typeIs('parents_policy'),
      },
      {
        key: 'room_rent_limit',
        label: 'Room Rent Limit',
        type: 'currency',
        showIf: typeNot('term_life'),
      },
      {
        key: 'corporate_buffer',
        label: 'Corporate Buffer',
        type: 'currency',
        showIf: typeIs('group_mediclaim', 'top_up'),
      },
      {
        key: 'co_payment_percentage',
        label: 'Co-payment %',
        type: 'percent',
        showIf: typeNot('term_life'),
        requiredIf: typeIs('group_mediclaim'),
      },
      {
        key: 'maternity_coverage',
        label: 'Maternity Coverage',
        type: 'boolean',
        showIf: typeIs('group_mediclaim'),
      },
      {
        key: 'maternity_sub_limit',
        label: 'Maternity Sub-limit',
        type: 'currency',
        showIf: (f) => f.policy_type === 'group_mediclaim' && f.maternity_coverage,
      },
      {
        key: 'opd_coverage',
        label: 'OPD Coverage',
        type: 'boolean',
        showIf: typeIs('group_mediclaim'),
      },
      {
        key: 'ambulance_cover_limit',
        label: 'Ambulance Cover Limit',
        type: 'currency',
        showIf: typeIs('group_mediclaim'),
      },
      {
        key: 'wellness_benefit',
        label: 'Wellness / Preventive Benefit',
        type: 'boolean',
        showIf: typeIs('group_mediclaim'),
      },
      {
        key: 'e_card_enabled',
        label: 'E-Card Enabled',
        type: 'boolean',
        showIf: typeIs('group_mediclaim'),
      },
      {
        key: 'digital_claim_enabled',
        label: 'Digital Claim Submission',
        type: 'boolean',
        showIf: typeIs('group_mediclaim'),
      },
      {
        key: 'cashless_hospitals',
        label: 'Cashless Hospitals',
        type: 'textarea',
        col: 2,
        showIf: typeIs('group_mediclaim', 'parents_policy', 'top_up'),
      },
      {
        key: 'day_care_procedures',
        label: 'Day Care Procedures',
        type: 'textarea',
        col: 2,
        showIf: typeIs('group_mediclaim'),
      },
      {
        key: 'pre_existing_disease_rules',
        label: 'Pre-existing Disease Rules',
        type: 'textarea',
        col: 2,
        showIf: typeIs('group_mediclaim', 'parents_policy', 'top_up'),
      },
    ],
  },
  {
    id: 'claims',
    title: 'Claims Configuration',
    fields: [
      { key: 'claim_settlement_tat', label: 'Claim Settlement TAT (days)', type: 'number' },
    ],
  },
  {
    id: 'eligibility',
    title: 'Enrollment Eligibility',
    description: 'BRD M3 rules — probation, contract, dependents',
    fields: [
      { key: 'probation_days', label: 'Probation Wait (days)', type: 'number' },
      {
        key: 'max_child_age',
        label: 'Max Child Age',
        type: 'number',
        showIf: typeIs('group_mediclaim', 'top_up'),
      },
      {
        key: 'max_parent_age',
        label: 'Max Parent Age',
        type: 'number',
        showIf: typeIs('parents_policy'),
        requiredIf: typeIs('parents_policy'),
      },
      {
        key: 'lwp_suspend_days',
        label: 'LWP Suspend Threshold (days)',
        type: 'number',
        showIf: typeNot('term_life'),
      },
      { key: 'contract_staff_eligible', label: 'Contract Staff Eligible', type: 'boolean' },
      {
        key: 'in_laws_eligible',
        label: 'In-laws Eligible',
        type: 'boolean',
        showIf: typeIs('group_mediclaim', 'parents_policy', 'top_up'),
      },
    ],
  },
  {
    id: 'contacts',
    title: 'HR & Coordinator',
    fields: [
      { key: 'hr_contact_person', label: 'HR Contact Person', type: 'text', required: true },
      { key: 'insurance_coordinator', label: 'Insurance Coordinator', type: 'text', required: true },
    ],
  },
];

export const EMPTY_POLICY_FORM = {
  company_name: '',
  group_company_name: '',
  branch: '',
  gst_number: '',
  hr_contact_person: '',
  insurance_coordinator: '',
  policy_start_date: '',
  policy_end_date: '',
  insurance_provider_id: '',
  policy_number: '',
  policy_type: 'group_mediclaim',
  sum_insured: '',
  room_rent_limit: '',
  cashless_hospitals: '',
  pre_existing_disease_rules: '',
  waiting_period_days: 30,
  maternity_coverage: false,
  maternity_sub_limit: '',
  corporate_buffer: '',
  claim_settlement_tat: 15,
  day_care_procedures: '',
  co_payment_percentage: 0,
  probation_days: 90,
  contract_staff_eligible: true,
  max_child_age: 25,
  in_laws_eligible: true,
  lwp_suspend_days: 30,
  opd_coverage: false,
  ambulance_cover_limit: '',
  e_card_enabled: true,
  digital_claim_enabled: true,
  wellness_benefit: false,
  deductible_amount: '',
  restoration_benefit: false,
  max_parent_age: '',
  floater_type: 'floater',
  free_cover_limit: '',
  max_sum_assured: '',
  ci_rider_included: false,
  accident_rider_included: false,
  grace_period_days: 30,
};

export function isFieldRequired(field, form) {
  if (typeof field.requiredIf === 'function') return Boolean(field.requiredIf(form));
  return Boolean(field.required);
}

export function isFieldVisible(field, form) {
  return !field.showIf || field.showIf(form);
}

export function policyToForm(policy) {
  if (!policy) return { ...EMPTY_POLICY_FORM };
  const form = { ...EMPTY_POLICY_FORM };
  for (const key of Object.keys(form)) {
    if (policy[key] !== undefined && policy[key] !== null) {
      if (typeof form[key] === 'boolean') {
        form[key] = Boolean(policy[key]);
      } else if (key.includes('date') && policy[key]) {
        form[key] = String(policy[key]).slice(0, 10);
      } else {
        form[key] = policy[key];
      }
    }
  }
  if (policy.insurance_provider_id) {
    form.insurance_provider_id = String(policy.insurance_provider_id);
  }
  return form;
}

function parseOptionalFloat(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

function parseOptionalInt(value) {
  if (value === '' || value === null || value === undefined) return null;
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? null : n;
}

export function formToPayload(form) {
  const policyType = form.policy_type || 'group_mediclaim';
  const payload = {
    ...form,
    insurance_provider_id: parseInt(form.insurance_provider_id, 10),
    sum_insured: parseFloat(form.sum_insured),
    room_rent_limit: parseOptionalFloat(form.room_rent_limit),
    maternity_sub_limit: parseOptionalFloat(form.maternity_sub_limit),
    corporate_buffer: parseOptionalFloat(form.corporate_buffer),
    waiting_period_days: parseInt(form.waiting_period_days, 10) || 0,
    claim_settlement_tat: parseOptionalInt(form.claim_settlement_tat),
    co_payment_percentage: parseFloat(form.co_payment_percentage) || 0,
    probation_days: parseInt(form.probation_days, 10) || 90,
    max_child_age: parseOptionalInt(form.max_child_age) ?? 25,
    lwp_suspend_days: parseInt(form.lwp_suspend_days, 10) || 30,
    ambulance_cover_limit: parseOptionalFloat(form.ambulance_cover_limit),
    deductible_amount: parseOptionalFloat(form.deductible_amount),
    max_parent_age: parseOptionalInt(form.max_parent_age),
    free_cover_limit: parseOptionalFloat(form.free_cover_limit),
    max_sum_assured: parseOptionalFloat(form.max_sum_assured),
    grace_period_days: parseOptionalInt(form.grace_period_days),
    floater_type: form.floater_type || null,
  };

  const toClear = TYPE_NULL_FIELDS[policyType] || [];
  for (const key of toClear) {
    if (BOOLEAN_NULL_DEFAULTS.has(key)) {
      payload[key] = false;
    } else if (key === 'co_payment_percentage') {
      payload[key] = 0;
    } else {
      payload[key] = null;
    }
  }

  return payload;
}

export function getPolicyTypeMeta(type) {
  return POLICY_TYPE_OPTIONS.find((o) => o.value === type) || POLICY_TYPE_OPTIONS[0];
}

export function getPolicyProgress(start, end) {
  const s = new Date(start);
  const e = new Date(end);
  const now = new Date();
  const total = e - s;
  const elapsed = now - s;
  if (total <= 0) return { percent: 0, daysLeft: 0, status: 'expired' };
  const percent = Math.min(100, Math.max(0, Math.round((elapsed / total) * 100)));
  const daysLeft = Math.ceil((e - now) / (1000 * 60 * 60 * 24));
  const status = daysLeft < 0 ? 'expired' : daysLeft <= 60 ? 'renewal' : 'active';
  return { percent, daysLeft: Math.max(0, daysLeft), status };
}
